import {
  TransformedValues,
  UseFormReturnType,
  createFormContext,
  hasLength,
  isEmail,
  isInRange,
  isNotEmpty,
  matches,
  matchesField,
  useField,
  useForm,
} from '@mantine/form';

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const phoneNumberRegex = /^\d{10}$/;

/**
 * Combines multiple validators into a single validator function.
 *
 * This utility allows you to chain multiple validation rules together for a single form field.
 * Validators are executed in sequence (left to right), and the function returns the first error
 * encountered or null if all validations pass.
 *
 * @param validators - An array of validator functions to be executed in order
 * @returns A combined validator function that returns the first error message or null if valid
 *
 * @example
 * // Basic usage with multiple validators
 * const form = useForm({
 *   validate: {
 *     name: combineValidators(
 *       isNotEmpty('Name is required'),
 *       hasLength({ min: 2 }, 'Name must be at least 2 characters long')
 *     ),
 *     email: combineValidators(
 *       isNotEmpty('Email is required'),
 *       isEmail('Invalid email format')
 *     )
 *   },
 * });
 *
 * @example
 * // With custom validator functions
 * const form = useForm({
 *   validate: {
 *     password: combineValidators(
 *       isNotEmpty('Password is required'),
 *       hasLength({ min: 8 }, 'Password must be at least 8 characters'),
 *       (value) => !/[A-Z]/.test(String(value)) ? 'Must contain uppercase letter' : null
 *     )
 *   }
 * });
 */
const combineValidators =
  (...validators: Array<(value: unknown) => React.ReactNode>) =>
  (value: unknown): React.ReactNode | null => {
    const errors = validators
      .map((validator) => validator(value))
      .filter((result) => result !== null);

    return errors.length > 0 ? errors[0] : null;
  };

export {
  combineValidators,
  createFormContext,
  emailRegex,
  hasLength,
  isEmail,
  isInRange,
  isNotEmpty,
  matches,
  matchesField,
  phoneNumberRegex,
  useField,
  useForm,
};
export type { TransformedValues, UseFormReturnType };

// Will move this to core
